import { useState, useEffect } from 'react';
import { onAuthStateChanged} from 'firebase/auth';
import { auth } from '../authConfig';
import { authService } from '../services/authService';
import { PERMISSIONS } from '../constants/permissions';

export const useAuthSync = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [activeJiraAccount, setActiveJiraAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [jiraAccountStatus, setJiraAccountStatus] = useState({
        hasAccounts: false,
        hasAccountsButNoneActive: false,
        activeAccountConnectionFailed: false
    });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (!firebaseUser) {
                setIsAuthenticated(false);
                setUser(null);
                setLoading(false);
                return;
            }

            try {
                await firebaseUser.getIdToken();
                setIsAuthenticated(true);

                try {
                    const meData = await authService.me();
                    setUser({
                        id: meData.user?.id,
                        displayName: meData.user?.display_name,
                        role: meData.user?.role || 'user',
                        email: meData.user?.email,
                    });
                    setPermissions(meData.permissions || []);
                    if (meData.active_jira_account) {
                        setActiveJiraAccount(meData.active_jira_account);
                        setJiraAccountStatus(prev => ({ ...prev, hasAccounts: true }));
                    }
                } catch (err) {
                    console.error('Failed to load user data from backend:', err);
                    setUser({
                        displayName: firebaseUser.displayName,
                        role: 'user',
                        email: firebaseUser.email
                    });
                    setPermissions([PERMISSIONS.WORKLOG]);
                }
            } catch (err) {
                console.error('Firebase auth error:', err);
                setIsAuthenticated(false);
            } finally {
                setLoading(false);
            }
        });

        // Re-validate on window focus
        const handleFocus = async () => {
            if (!auth.currentUser) return;
            try {
                // Refresh token on focus
                await auth.currentUser.getIdToken(true);
                const meData = await authService.me();
                setUser({
                    id: meData.user?.id,
                    displayName: meData.user?.display_name,
                    role: meData.user?.role || 'user',
                    email: meData.user?.email,
                });
                setPermissions(meData.permissions || []);
                if (meData.active_jira_account) {
                    setActiveJiraAccount(meData.active_jira_account);
                }
            } catch (err) {
                console.error('Failed to refresh user data on focus:', err);
            }
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            unsubscribe();
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    return {
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        permissions,
        setPermissions,
        activeJiraAccount,
        setActiveJiraAccount,
        loading,
        setLoading,
        error,
        setError,
        jiraAccountStatus,
        setJiraAccountStatus
    };
};